import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDescope } from '@descope/react-sdk';
import * as Sentry from '@sentry/react';
import {
  listUserOrganizations,
  getOrganization,
  createOrganization,
  handleInvite,
  updateOrganizationName,
  getInviteInfoByToken,
  respondToInviteByToken,
} from '@kirin/api-client/api/organizations';

import {
  InviteAction,
  type UpdatedOrganizationNameType,
} from '@kirin/api-client/contracts/organizations';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { cacheConfigForStaticReferenceData } from './queryConfig';
import { retryHandlers } from './helpers';

/**
 * Hook to fetch user's organizations list
 *
 * Uses React Query for caching and automatic refetching.
 * Organizations are cached for 5 minutes.
 *
 * @example
 * const { data: organizations, isLoading, error } = useOrganizations();
 *
 * @returns React Query result with organizations array
 */
export function useOrganizations() {
  return useQuery({
    queryKey: queryKeys.organizations.list(),
    queryFn: () => listUserOrganizations(apiClient),
    retry: retryHandlers.list,
  });
}

/**
 * Hook to fetch a single organization by ID
 *
 * Uses React Query for caching and automatic refetching.
 * Organization details are cached for 10 minutes.
 *
 * @param organizationId - The organization ID to fetch
 * @param enabled - Whether to enable the query (default: true if organizationId provided)
 *
 * @example
 * const { data: organization, isLoading, error } = useOrganization(orgId);
 *
 * @returns React Query result with organization object
 */
export function useOrganization(
  organizationId: string | undefined,
  enabled = true
) {
  const isEnabled = enabled && !!organizationId;
  return useQuery({
    queryKey: queryKeys.organizations.detail(organizationId ?? ''),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return getOrganization(apiClient, organizationId);
    },
    enabled: isEnabled,
    ...cacheConfigForStaticReferenceData,
    retry: retryHandlers.detail,
  });
}

/**
 * Hook to create a new organization
 *
 * Uses React Query mutation with automatic cache invalidation and token refresh.
 * The mutation function handles both organization creation and Descope token refresh,
 * ensuring the access token includes the new organization before mutateAsync resolves.
 *
 * @example
 * const { mutateAsync, isPending, error } = useCreateOrganization();
 * const org = await mutateAsync('My Organization');
 * // Token is already refreshed at this point, safe to navigate
 *
 * @returns React Query mutation result
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient();
  const sdk = useDescope();

  return useMutation({
    mutationFn: async (name: string) => {
      // Create the organization
      const organization = await createOrganization(apiClient, { name });

      try {
        // Refresh the Descope session to get updated access token with new organization
        // This prevents access denied errors when navigating to the new organization
        await sdk.refresh();
      } catch (error) {
        // Log token refresh failure to Sentry
        Sentry.captureException(error, {
          tags: {
            error_type: 'token_refresh_failed',
            context: 'create_organization',
          },
          extra: {
            organizationId: organization.id,
            organizationName: organization.name,
            message:
              'Token refresh failed after creating organization, forcing page reload',
          },
          level: 'warning',
        });
      }

      // Force a page reload to ensure user data is fresh with new organization roles
      // The Descope useUser() hook doesn't always update synchronously after sdk.refresh()
      // causing role-based access checks to fail and show blank page
      window.location.href = `/${organization.id}`;

      // This return won't be reached due to navigation, but TypeScript needs it
      return organization;
    },
    onSuccess: () => {
      // Invalidate organizations list cache after successful creation
      // Note: This may not run since window.location.href navigates away immediately
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });
    },
  });
}

/**
 * Hook to handle an organization invite (accept or reject)
 *
 * Uses React Query mutation with automatic cache invalidation and token refresh.
 * For accept actions, handles Descope token refresh and navigates to the accepted organization.
 * For reject actions, just invalidates the organizations list cache.
 *
 * @example
 * const { mutateAsync: respondToInvite, isPending, error } = useInvitationResponse();
 * // Accept invite
 * await respondToInvite({ organizationId: 'org-123', action: InviteAction.Accept });
 * // Reject invite
 * await respondToInvite({ organizationId: 'org-123', action: InviteAction.Reject });
 *
 * @returns React Query mutation result
 */
export function useInvitationResponse() {
  const queryClient = useQueryClient();
  const sdk = useDescope();

  return useMutation({
    mutationFn: async ({
      organizationId,
      action,
    }: {
      organizationId: string;
      action: InviteAction;
    }) => {
      // Handle the invite action (accept or reject) - API call happens for both actions
      await handleInvite(apiClient, organizationId, action);

      if (action === InviteAction.Accept) {
        // For accept: refresh token and navigate to the organization
        try {
          // Refresh the Descope session to get updated access token with new organization
          // This prevents access denied errors when navigating to the accepted organization
          await sdk.refresh();
        } catch (error) {
          // Log token refresh failure to Sentry
          Sentry.captureException(error, {
            tags: {
              error_type: 'token_refresh_failed',
              context: 'accept_invite',
            },
            extra: {
              organizationId,
              message:
                'Token refresh failed after accepting invite, forcing page reload',
            },
            level: 'warning',
          });
        }

        // Force a page reload to ensure user data is fresh with new organization roles
        // The Descope useUser() hook doesn't always update synchronously after sdk.refresh()
        // causing role-based access checks to fail and show blank page
        window.location.href = `/${organizationId}`;

        // This return won't be reached due to navigation, but TypeScript needs it
        return undefined;
      } else {
        // For reject: API call already completed above, just return (no navigation needed)
        return undefined;
      }
    },
    onSuccess: () => {
      // Invalidate organizations list cache after successful action
      // Note: For accept, this may not run since window.location.href navigates away immediately
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });
    },
  });
}

/**
 * Hook to resolve an invite token to org name + role (KIR-3786).
 *
 * Powers the invite deep-link flow: the token (not the user's org list) is the
 * source of truth, so this works even when the user's Descope identity differs
 * from the one the invite was originally created for.
 *
 * @param token - The invite token from the `?invite_token=` query param
 * @returns React Query result with the resolved invite info
 */
export function useInviteTokenInfo(token: string | null) {
  return useQuery({
    queryKey: queryKeys.organizations.inviteToken(token ?? ''),
    queryFn: () => {
      if (!token) throw new Error('token is required');
      return getInviteInfoByToken(apiClient, token);
    },
    enabled: !!token,
    retry: false,
  });
}

/**
 * Mutation hook to respond to an invite by token (accept or reject) (KIR-3786).
 *
 * On accept, refreshes the Descope session and navigates into the org named by
 * the token. On reject, invalidates the organizations list cache.
 *
 * @returns React Query mutation result
 */
export function useRespondToInviteByToken() {
  const queryClient = useQueryClient();
  const sdk = useDescope();

  return useMutation({
    mutationFn: async ({
      token,
      action,
    }: {
      token: string;
      action: InviteAction;
    }) => {
      const result = await respondToInviteByToken(apiClient, token, action);

      if (action === InviteAction.Accept && result.organizationId) {
        try {
          // Refresh the Descope session so the access token includes the org.
          await sdk.refresh();
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              error_type: 'token_refresh_failed',
              context: 'accept_invite_by_token',
            },
            extra: {
              organizationId: result.organizationId,
              message:
                'Token refresh failed after accepting invite by token, forcing page reload',
            },
            level: 'warning',
          });
        }

        // Force a page reload so role-based access checks see fresh org roles.
        window.location.href = `/${result.organizationId}`;
        return undefined;
      }

      return undefined;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });
    },
  });
}

/**
 * Mutation hook to update organization name
 *
 * @param organizationId - The organization ID to update
 * @returns React Query mutation result
 */
export function useUpdateOrganizationName(organizationId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return await updateOrganizationName(apiClient, organizationId, name);
    },
    onSuccess: (data: UpdatedOrganizationNameType) => {
      queryClient.setQueryData(
        queryKeys.organizations.detail(data.id),
        (oldData: UpdatedOrganizationNameType) => {
          if (!oldData) return data; // this shouldn't happen but its a guard
          return {
            ...oldData,
            name: data.name,
          };
        }
      );
      // Invalidate all organizations and organization detail queries
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.list(),
      });
    },
  });
}
